import { nanoid } from 'nanoid'
import { Actor, Fight, FightData, Spell, SpellAssignment } from '../redux/models'
import { expectOneById, idMapper, jsonClone, nextOrdinal } from '../util/utils'

type CreateActorPayload = {
    isBoss: boolean
    name: string
    fightId: string
    ordinal: number
    color?: string
}

type CreateSpellPayload = {
    actorId: string
    name: string
    ordinal: number
    cooldown: number
    duration: number
    ramp: number
}

type CreateAssignmentPayload = {
    spellId: string
    offset: number
    hideTimestamp?: boolean
    overrideCooldown?: number
    overrideDuration?: number
    overrideRamp?: number
}

type CreateFightPayload = {
    name: string
}

export class EntityCreator {

    public static cloneFightData(fightData: FightData): FightData {
        const rawClone = jsonClone(fightData)

        rawClone.fight.id = nanoid(10)
        rawClone.fight.createdAt = new Date().toISOString()
        rawClone.fight.updatedAt = new Date().toISOString()

        const actorIdMapper = idMapper()
        const spellIdMapper = idMapper()

        rawClone.actors.forEach(actor => {
            actor.fightId = rawClone.fight.id
            actorIdMapper.newId(actor)
        })

        rawClone.spells.forEach(spell => {
            spell.actorId = actorIdMapper.swap(spell.actorId)
            spellIdMapper.newId(spell)
        })

        rawClone.assignments.forEach(assign => {
            assign.spellId = spellIdMapper.swap(assign.spellId)
            assign.id = nanoid(10)
        })

        return rawClone
    }

    public static cloneActor(fightData: FightData, actorId: string): {
        actor: Actor
        spells: Spell[]
        assignments: SpellAssignment[]
    } {

        const actorClone = jsonClone(expectOneById(fightData.actors, actorId))

        const spellsClone = jsonClone(fightData.spells
            .filter(spell => spell.actorId === actorClone.id))

        const assignmentsClone = jsonClone(fightData.assignments
            .filter(assign => spellsClone.map(spell => spell.id).includes(assign.spellId)))

        actorClone.id = nanoid(10)
        actorClone.ordinal = nextOrdinal(fightData.actors)

        const spellIdMapper = idMapper()

        spellsClone.forEach(spell => {
            spell.actorId = actorClone.id
            spellIdMapper.newId(spell)
        })

        assignmentsClone.forEach(assign => {
            assign.id = nanoid(10)
            assign.spellId = spellIdMapper.swap(assign.spellId)
        })

        return {
            actor: actorClone,
            spells: spellsClone,
            assignments: assignmentsClone
        }
    }

    public static createFight(payload: CreateFightPayload): Fight {
        return {
            id: nanoid(10),
            name: payload.name,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        }
    }

    public static createActor(payload: CreateActorPayload): Actor {
        return {
            fightId: payload.fightId,
            id: nanoid(10),
            name: payload.name,
            color: payload.color ?? '#000',
            hidden: false,
            isBoss: payload.isBoss,
            ordinal: payload.ordinal,
        }
    }

    public static createSpell(payload: CreateSpellPayload): Spell {
        return {
            actorId: payload.actorId,
            id: nanoid(10),
            name: payload.name,
            ordinal: payload.ordinal,
            color: '#000',
            cooldown: payload.cooldown,
            duration: payload.duration,
            ramp: payload.ramp,
        }
    }

    public static createAssignment(payload: CreateAssignmentPayload): SpellAssignment {
        return {
            spellId: payload.spellId,
            id: nanoid(10),
            offset: payload.offset,
            hideTimestamp: payload.hideTimestamp,
            overrideCooldown: payload.overrideCooldown,
            overrideDuration: payload.overrideDuration,
            overrideRamp: payload.overrideRamp
        }
    }

}
